import { useEffect, useLayoutEffect, useRef } from 'react';
import styles from './DocNotes.module.css';
import { cx } from '../../lib/cx';

/**
 * Volitelný rámeček s volným textem — poznámka, připomínka, vyjádření TDS…
 * Používá se v sekci „Ostatní" předávacích protokolů: uživatel si přidá tolik
 * rámečků, kolik potřebuje, každý má vlastní nadpis a rostoucí textové pole.
 * Když žádný rámeček není, sekce se v dokumentu (ani v PDF) vůbec neobjeví.
 */
export interface DocNote {
  id: string;
  nadpis: string;
  text: string;
}

/** Vyrobí prázdný rámeček (id stačí unikátní v rámci dokumentu). */
export function createDocNote(nadpis = ''): DocNote {
  return {
    id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    nadpis,
    text: '',
  };
}

export interface DocNotesProps {
  items: DocNote[];
  onChange: (id: string, patch: Partial<DocNote>) => void;
  onRemove: (id: string) => void;
  titlePlaceholder?: string;
  textPlaceholder?: string;
}

export function DocNotes({
  items,
  onChange,
  onRemove,
  titlePlaceholder = 'Nadpis (např. Vyjádření TDS, Poznámka…)',
  textPlaceholder = 'Text — poznámka, připomínka, ujednání…',
}: DocNotesProps) {
  return (
    <>
      {items.map((n) => (
        <NoteBox
          key={n.id}
          note={n}
          onChange={onChange}
          onRemove={onRemove}
          titlePlaceholder={titlePlaceholder}
          textPlaceholder={textPlaceholder}
        />
      ))}
    </>
  );
}

function NoteBox({
  note,
  onChange,
  onRemove,
  titlePlaceholder,
  textPlaceholder,
}: {
  note: DocNote;
  onChange: (id: string, patch: Partial<DocNote>) => void;
  onRemove: (id: string) => void;
  titlePlaceholder: string;
  textPlaceholder: string;
}) {
  // Textarea roste podle obsahu — stejně jako víceřádkový DocRow, aby byl
  // v náhledu i v PDF vidět celý text.
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const grow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  useLayoutEffect(grow, [note.text]);
  useEffect(() => {
    window.addEventListener('resize', grow);
    return () => window.removeEventListener('resize', grow);
  }, []);

  return (
    <div className={styles.note}>
      <div className={styles.head}>
        <input
          type="text"
          className={styles.title}
          value={note.nadpis}
          onChange={(e) => onChange(note.id, { nadpis: e.target.value })}
          placeholder={titlePlaceholder}
        />
        <button
          type="button"
          className={cx(styles.remove, 'docNoPrint')}
          onClick={() => onRemove(note.id)}
          aria-label="Odebrat rámeček"
          title="Odebrat rámeček"
        >
          ✕
        </button>
      </div>
      <textarea
        ref={taRef}
        className={styles.text}
        value={note.text}
        onChange={(e) => onChange(note.id, { text: e.target.value })}
        placeholder={textPlaceholder}
        rows={2}
      />
    </div>
  );
}
